import React, { useContext, useState } from 'react';

import TrainWeeklySchedule from '../../components/TrainWeeklySchedule';
import StationInfoContext from '../StationInfoContext';

import { TrainDetailsData } from '../../types';

import isSSR from '../../../utils/isSSR';
import { crawlerDuplicateAvoidStyle, isMobile } from '../../../utils/commonUtils';
import { trackClickEventProp61 } from '../../../utils/omnitureUtils';
import { SEO_STATION_PAGE } from '../../../utils/RailsSEOConstants';

type StationInfoListItemProps = {
  train: TrainDetailsData;
};
const StationInfoMobileListItem = ({ train }: StationInfoListItemProps) => {
  const stationData = useContext(StationInfoContext);
  return (
    <div
      role="button"
      onClick={() => stationData.onTrainClicked(train, isMobile())}
      className="railSeoWrap__infoTable--info"
    >
      <div>
        <h4 className="railSeoWrap__infoTable--railName">
          <a className="seoName" onClick={(e) => e.preventDefault()} href={train.trainPageUrl}>
            {train.trainName}
          </a>
        </h4>
        <div style={{ width: '80px' }}>
          <span style={{ display: 'table', marginLeft: 'auto' }} className="railSeoWrap__infoTable--railNo">
            {`# ${train.trainNumber}`}
          </span>
        </div>
      </div>
      <div style={crawlerDuplicateAvoidStyle} className="mobileShow appendBottom10">
        <span className="railSeoWrap__infoTable--srvcTxt">{stationData.miscellaneous['SERVICE DAYS']} : </span>
        <TrainWeeklySchedule trainSchedule={train.trainWeeklySchedule} />
      </div>
      <div className="railSeoWrap__infoTable--time">
        <a className="seoLink" onClick={(e) => e.preventDefault()} href={train.routePageUrl}>
          <div className="railSeoWrap__infoTable--left">
            <span className="railSeoWrap__infoTable--startTime"> {train.departureTime}</span>
            <span className="railSeoWrap__infoTable--stn">
              <span className="railSeoWrap__infoTable--stnShrtName"> {train.sourceStn}</span>
              <span className="railSeoWrap__infoTable--stnFullNm">{train.sourceStnName}</span>
            </span>
          </div>
          <div className="railSeoWrap__infoTable--mid">
            <span className="hrLine80">&nbsp;</span>
            <span className="appendLeft5 appendRight5 duration"> {train.duration}</span>
            <span className="hrLine80">&nbsp;</span>
          </div>
          <div className="railSeoWrap__infoTable--right">
            <span className="railSeoWrap__infoTable--endTime"> {train.arrivalTime}</span>
            <span className="railSeoWrap__infoTable--stn">
              <span className="railSeoWrap__infoTable--stnShrtName">{train.destinationStn}</span>
              <span className="railSeoWrap__infoTable--stnFullNm">{train.destinationStnName}</span>
            </span>
          </div>
        </a>
      </div>
    </div>
  );
};

const StationInfoDesktopListItem = ({ train }: StationInfoListItemProps) => {
  const stationData = useContext(StationInfoContext);
  return (
    <div
      className="for_station_wrapper"
      onClick={() => {
        trackClickEventProp61(SEO_STATION_PAGE, 'rail_seo_station_trainroutecard_clicked');
      }}
    >
      <div className="trainInfo">
        <h4 className="railSeoWrap__infoTable--railName">
          <a className="seoName" onClick={(e) => e.preventDefault()} href={train.trainPageUrl}>
            {train.trainName}
          </a>
        </h4>
        <span className="railSeoWrap__infoTable--railNo">{`# ${train.trainNumber}`}</span> <br />
        <span className="railSeoWrap__infoTable--srvcTxt">{stationData.miscellaneous['SERVICE DAYS']} : </span>
        <TrainWeeklySchedule trainSchedule={train.trainWeeklySchedule} />
      </div>
      <div className="railSeoWrap__infoTable--time">
        <a className="seoLink" onClick={(e) => e.preventDefault()} href={train.routePageUrl}>
          <div className="railSeoWrap__infoTable--left">
            <span className="railSeoWrap__infoTable--startTime">{train.departureTime}</span>
            <span className="railSeoWrap__infoTable--stn">
              <span className="railSeoWrap__infoTable--stnShrtName">{train.sourceStn}</span>
              <span className="railSeoWrap__infoTable--stnFullNm">{train.sourceStnName}</span>
            </span>
          </div>
          <div className="railSeoWrap__infoTable--mid drclmn" style={{ paddingBottom: 10 }}>
            <span className="trvl_tm">
              <span className="hrLine80">&nbsp;</span>
              <span className="appendLeft5 appendRight5 duration">{train.duration}</span>
              <span className="hrLine80">&nbsp;</span>
            </span>

            <span className="stp_type darkGreyText">
              {train.stops} stops, {train.distance}
            </span>
          </div>
          <div className="railSeoWrap__infoTable--right">
            <span className="railSeoWrap__infoTable--endTime">{train.arrivalTime}</span>
            <span className="railSeoWrap__infoTable--stn">
              <span className="railSeoWrap__infoTable--stnShrtName">{train.destinationStn}</span>
              <span className="railSeoWrap__infoTable--stnFullNm">{train.destinationStnName}</span>
            </span>
          </div>
        </a>
      </div>
      <div className="bookButton">
        <button
          style={{ width: '118px' }}
          onClick={() => stationData.onTrainClicked(train, isMobile())}
          className="primaryBtn bkTrnBtn"
        >
          {stationData.miscellaneous['BOOK']}
        </button>
      </div>
    </div>
  );
};

type StationInfoListProps = {
  title: string;
  trains: TrainDetailsData[];
};

export default function StationInfoList({ title, trains }: StationInfoListProps) {
  const stationData = useContext(StationInfoContext);
  const [showFullTrains, setShowFullTrains] = useState(isSSR());
  return (
    <>
      {trains.length === 0 ? (
        <></>
      ) : (
        <>
          <section className="mobileShow d-relative">
            <div className="railSeoWrap__infoTable">
              <h3 className="mobileShow font22 latoLight">{stationData.miscellaneous['TRAIN ROUTES']}</h3>
              <h3 style={crawlerDuplicateAvoidStyle} className="railSeoWrap__infoTable--title mobileShow">
                {title}
              </h3>
              <div suppressHydrationWarning>
                {trains
                  .filter((_, index) => showFullTrains || index < 10)
                  .map((t) => (
                    <StationInfoMobileListItem key={t.trainNumber} train={t} />
                  ))}
              </div>
            </div>
            <div
              className="stationsShowMoreButton"
              onClick={() => {
                trackClickEventProp61(SEO_STATION_PAGE, 'rail_seo_station_trainroutecard_viewmore_clicked');
              }}
            >
              {trains.length > 10 && (
                <button
                  suppressHydrationWarning
                  className="primaryBtn bkTrnBtn bottom_fix"
                  onClick={() => setShowFullTrains(!showFullTrains)}
                >
                  {showFullTrains ? stationData.miscellaneous['VIEW LESS'] : stationData.miscellaneous['VIEW MORE']}
                </button>
              )}
            </div>
          </section>
          <section className="desktopShow d-relative">
            <h3 className="railSeoWrap__infoTable--title desktopShow capText no_margn">{title}</h3>
            <div className="railSeoWrap__infoTable">
              <p className="railSeoWrap__railstation--rwTitle">
                <span className="train_detls">{stationData.trainDetailsColumn['trainDetails']}</span>
                <span className="surce_depr">{stationData.trainDetailsColumn['sourceDeparture']}</span>
                <span className="jurny_detls">{stationData.trainDetailsColumn['journeyDetails']}</span>
                <span className="destn_arrvl">{stationData.trainDetailsColumn['destinationArrival']}</span>
              </p>
              <div className="railSeoWrap__infoTable--info for_station" suppressHydrationWarning>
                {trains
                  .filter((_, index) => showFullTrains || index < 10)
                  .map((t) => (
                    <StationInfoDesktopListItem key={t.trainNumber} train={t} />
                  ))}
              </div>
            </div>
            <div
              className="stationsShowMoreButton"
              onClick={() => {
                trackClickEventProp61(SEO_STATION_PAGE, 'rail_seo_station_trainroutecard_viewmore_clicked');
              }}
            >
              {trains.length > 10 && (
                <button
                  suppressHydrationWarning
                  className="primaryBtn bkTrnBtn bottom_fix"
                  onClick={() => setShowFullTrains(!showFullTrains)}
                >
                  {showFullTrains ? stationData.miscellaneous['VIEW LESS'] : stationData.miscellaneous['VIEW MORE']}
                </button>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}
